import React, { useContext, useState } from "react";
import { useHistory } from "react-router";
import * as queryString from "query-string";
import { unwrapResult } from "@reduxjs/toolkit";
import { CustomerModel } from "types/model/customer";
import { Button, Input, Modal as AntdModal, Space } from "antd";
import { message, notification, Modal } from "components";
import { useAppDispatch } from "app/store";
import useAppAuth0 from "hooks/useAppAuth0";
import { fetchCustomers, getResetPasswordLink, removeCustomer, resetPassword } from "../customerSlice";
import { boldAndItalic } from "../../../utils";
import { CustomerListContext } from "./CustomerListContext";

interface CustomerActionProps {
  record: CustomerModel;
  isB2C: boolean;
}

function CustomerAction({ record, isB2C }: CustomerActionProps) {
  const { user } = useAppAuth0();
  const dispatch = useAppDispatch();
  const { email } = record;
  const { dataSource, query } = useContext(CustomerListContext);
  const history = useHistory();
  const [sendEmailModalVisible, setSendEmailModalVisible] = useState(false);
  const [generateLinkModalVisible, setGenerateLinkModalVisible] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  const sendResetPasswordEmail = () =>
    dispatch(resetPassword(email))
      .then(unwrapResult)
      .then(() => {
        notification.success({
          message: "Email Send！",
          description: `Reset password Email has been successfully send to ${email}`,
        });
      })
      .catch(message.error)
      .finally(() => setSendEmailModalVisible(false));

  const generateResetPasswordLink = () => {
    setGeneratingLink(true);
    dispatch(getResetPasswordLink(record.auth0Id))
      .then(unwrapResult)
      .then((link) => navigator.clipboard.writeText(link))
      .then(() => {
        notification.success({
          message: "Reset Link Generated",
          description: "Generated and copied. Previous reset links are now invalid.",
        });
        setGenerateLinkModalVisible(false);
      })
      .catch(message.error)
      .finally(() => setGeneratingLink(false));
  };

  const refetch = async () => {
    if (query.page && query.page >= 1 && dataSource.length <= 1) {
      return history.push({
        search: queryString.stringify(
          { ...query, page: query.page - 1 },
          {
            arrayFormat: "comma",
          }
        ),
      });
    }
    return unwrapResult(await dispatch(fetchCustomers(query)));
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Space size={12}>
        <Button type="link" disabled={!record.supportResetPassword} onClick={() => setSendEmailModalVisible(true)}>
          Reset Password
        </Button>
        <AntdModal
          title="Send email"
          visible={sendEmailModalVisible}
          centered
          onCancel={() => setSendEmailModalVisible(false)}
          footer={[
            <Button
              key="cancel"
              shape="round"
              style={{ minWidth: "92px" }}
              onClick={() => setSendEmailModalVisible(false)}
            >
              Cancel
            </Button>,
            <Button
              key="send"
              type="primary"
              shape="round"
              style={{ minWidth: "92px" }}
              onClick={sendResetPasswordEmail}
            >
              Send
            </Button>,
            <Button
              key="generate"
              shape="round"
              style={{ minWidth: "92px" }}
              onClick={() => {
                setSendEmailModalVisible(false);
                setGenerateLinkModalVisible(true);
              }}
            >
              Generate Reset Link
            </Button>,
          ]}
        >
          {`Are you sure you want to send reset password email to ${email}?`}
        </AntdModal>
        <AntdModal
          title="Generate New Reset Link"
          visible={generateLinkModalVisible}
          centered
          onCancel={() => setGenerateLinkModalVisible(false)}
          footer={[
            <Button
              key="back"
              shape="round"
              style={{ minWidth: "92px" }}
              onClick={() => {
                setGenerateLinkModalVisible(false);
                setSendEmailModalVisible(true);
              }}
            >
              Back
            </Button>,
            <Button
              key="generate"
              type="primary"
              shape="round"
              style={{ minWidth: "92px" }}
              loading={generatingLink}
              onClick={generateResetPasswordLink}
            >
              Generate
            </Button>,
          ]}
        >
          <Space direction="vertical" size={12}>
            {`Please confirm that ${email} matches the account requesting the password reset.`}
            {
              "Generating a new reset link will immediately expire all previous links. Sent links are valid for 5 days or until the user completes the password reset."
            }
          </Space>
        </AntdModal>
        {user.canDeleteCustomer && (
          <Button
            type="link"
            onClick={() => {
              const modal = Modal.confirm({
                title: "Confirm",
                content: (
                  <Space direction="vertical" size={12}>
                    {`Please be aware that we will delete all this customer's information, including Auth0 account, entitlement, learning progress, vocabulary. This can not be undone.`}
                    {boldAndItalic`To confirm deletion, type ${record.email}.`}
                    <Input
                      onChange={(e) =>
                        modal.update((prevConfig) => ({
                          ...prevConfig,
                          okButtonProps: {
                            ...prevConfig.okButtonProps,
                            disabled: e.target.value !== record.email,
                          },
                        }))
                      }
                    />
                  </Space>
                ),
                okText: "Delete",
                okButtonProps: {
                  disabled: true,
                  shape: "round",
                  style: { minWidth: "92px" },
                },
                onOk: () =>
                  dispatch(removeCustomer({ id: record.customerId, isB2C, email: record.email }))
                    .then(unwrapResult)
                    .then(refetch)
                    .catch(message.error),
              });
            }}
          >
            Delete
          </Button>
        )}
      </Space>
    </div>
  );
}

export default CustomerAction;
